import type { AiModel } from './ai-model'
import type { GenerationMode } from './generation-mode'

export const MODEL_PRESENTATION_BADGES = ['RECOMMENDED', 'NEW', 'BETA', 'DEPRECATED'] as const
export const MODEL_PRESENTATION_POSITIONINGS = ['QUALITY', 'BALANCED', 'FAST', 'ECONOMY'] as const
export const MODEL_PRESENTATION_STRENGTHS = ['TEXT', 'PORTRAIT', 'CHINESE', 'INSTRUCTION', 'EDIT', 'REFERENCE', 'ECOMMERCE'] as const

export const MODEL_TAGS = {
  RECOMMENDED: { label: '推荐', type: 'status', tone: 'violet' },
  NEW: { label: 'NEW', type: 'status', tone: 'cyan' },
  BETA: { label: 'Beta', type: 'status', tone: 'amber' },
  DEPRECATED: { label: '即将下线', type: 'status', tone: 'orange' },

  QUALITY: { label: '高质量', type: 'positioning', tone: 'violet' },
  BALANCED: { label: '均衡', type: 'positioning', tone: 'slate' },
  FAST: { label: '快速', type: 'positioning', tone: 'emerald' },
  ECONOMY: { label: '省积分', type: 'positioning', tone: 'green' },

  TEXT: { label: '文字强', type: 'strength', tone: 'sky' },
  PORTRAIT: { label: '人像强', type: 'strength', tone: 'rose' },
  CHINESE: { label: '中文强', type: 'strength', tone: 'red' },
  INSTRUCTION: { label: '指令强', type: 'strength', tone: 'indigo' },
  EDIT: { label: '编辑强', type: 'strength', tone: 'purple' },
  REFERENCE: { label: '参考图强', type: 'strength', tone: 'teal' },
  ECOMMERCE: { label: '电商图强', type: 'strength', tone: 'amber' },
} as const

type ModelPresentationBadge = (typeof MODEL_PRESENTATION_BADGES)[number]
type ModelPresentationPositioning = (typeof MODEL_PRESENTATION_POSITIONINGS)[number]
type ModelPresentationStrength = (typeof MODEL_PRESENTATION_STRENGTHS)[number]
type ModelTagCode = keyof typeof MODEL_TAGS

function labelsFor<T extends ModelTagCode>(codes: readonly T[]) {
  return Object.fromEntries(codes.map((code) => [code, MODEL_TAGS[code].label])) as Record<T, string>
}

export const modelPresentationBadgeLabels = labelsFor<ModelPresentationBadge>(MODEL_PRESENTATION_BADGES)
export const modelPresentationPositioningLabels = labelsFor<ModelPresentationPositioning>(MODEL_PRESENTATION_POSITIONINGS)
export const modelPresentationStrengthLabels = labelsFor<ModelPresentationStrength>(MODEL_PRESENTATION_STRENGTHS)

export function getModelPresentation(model: AiModel, mode: GenerationMode) {
  return model.capabilityPresentations?.[mode]
}

export function getModelPresentationTags(model: AiModel, mode: GenerationMode) {
  const presentation = getModelPresentation(model, mode)
  const codes: ModelTagCode[] = []
  if (presentation?.badge) codes.push(presentation.badge)
  if (presentation?.positioning) codes.push(presentation.positioning)
  presentation?.strengths.forEach((strength) => codes.push(strength))
  return codes.slice(0, 2).map((code) => ({ code, ...MODEL_TAGS[code] }))
}

export function compareModelsForMode(left: AiModel, right: AiModel, mode: GenerationMode) {
  const leftPresentation = getModelPresentation(left, mode)
  const rightPresentation = getModelPresentation(right, mode)
  return (leftPresentation?.sortOrder ?? 0) - (rightPresentation?.sortOrder ?? 0)
    || left.name.localeCompare(right.name, 'zh-CN')
}
